import {
  ConfirmModal,
  SatisfactionSurvey,
} from "../../components";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TQuiz, TSurveyAnswer } from "../../types/Aula";
import { useForm } from "@mantine/form";
import { initSatisfactionSurvey } from "../../constants/SatisfactionSurvey";
import { validateArrayField, validateStringField } from "../../utils";
import { aulaService } from "../../api";
import { showNotification } from "@mantine/notifications";
import { TForm } from "../../types/Aula/TForm";
import { useMediaQuery } from "@mantine/hooks";
import { Flex } from "@mantine/core";
import { TLesson } from "../../types/Aula/TCourse";

export const SatisfactionSurveyPage = ({
  nextLesson,
}: {
  nextLesson: TLesson | null;
}) => {
  const { id } = useParams();
  const [satisfactionSurvey, setSatisfactionSurvey] = useState<TQuiz | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitValues, setSubmitValues] = useState<TSurveyAnswer>({});
  const isMobile = useMediaQuery("(max-width: 992px)");
  const navigate = useNavigate();

  const form = useForm<TForm>({
    initialValues: {},
    validate: (values: TForm) => {
      const errors: TSurveyAnswer = {};

      Object.keys(values).forEach((key) => {
        const value = values[key];
        let error;
        
        // Make open_answer questions optional
        const question = satisfactionSurvey?.questions.find((q) => q.id === key);
        if (question?.category === "open_answer") return;

        if (typeof value === "string") {
          error = validateStringField(value);
        } else if (Array.isArray(value)) {
          error = validateArrayField(value);
        }

        if (error) errors[key] = error;
      });

      return errors;
    },
  });

  useEffect(() => {
    const satisfactionSurveyWithChoices = initSatisfactionSurvey;
    satisfactionSurveyWithChoices.questions = satisfactionSurveyWithChoices.questions.map((question) => {
      if (question.category === "satisfaction_level") {
        question.choices = [
          { id: "Nada satisfecho/a", order: 1, description: "Nada satisfecho/a" },
          { id: "Poco satisfecho/a", order: 2, description: "Poco satisfecho/a" },
          { id: "Medianamente satisfecho/a", order: 3, description: "Medianamente satisfecho/a" },
          { id: "Totalmente satisfecho/a", order: 4, description: "Totalmente satisfecho/a" },
        ];
      }
      else if (question.category === "utility_level") {
        question.choices = [
          { id: "Nada útil", order: 1, description: "Nada útil" },
          { id: "Poco útil", order: 2, description: "Poco útil" },
          { id: "Medianamente útil", order: 3, description: "Medianamente útil" },
          { id: "Muy útil", order: 4, description: "Muy útil" },
        ];
      }
      else if (question.category.includes("from") && question.category.includes("to")) {
        const parts = question.category.split("_");
        const from = parseInt(parts[1]);
        const to = parseInt(parts[3]);
        question.choices = Array.from({ length: Number(to) - Number(from) + 1 }, (_, index) => {
          return {
            id: (index + Number(from)).toString(),
            order: index + Number(from),
            description: (index + Number(from)).toString(),
          };
        });
      }
      return question;
    });
    const initialValues: TForm = {};
    satisfactionSurveyWithChoices.questions.forEach((question) => {
      initialValues[question.id] = question.category === "multiple_choice" ? [] : "";
    });
    form.setValues(initialValues);
    setSatisfactionSurvey(satisfactionSurveyWithChoices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = (values: TSurveyAnswer) => {
    setSubmitValues(values);
    setConfirmModalOpen(true);
  }
  
  const handleSubmit = async () => {
    if (!form.validate().hasErrors && id) {
      try {
        await aulaService.postSatisfactionSurvey(id, submitValues);
        showNotification({
          title: "Éxito",
          message: "¡Encuesta de satisfacción completada!",
          color: "green",
        });
        if (nextLesson) {
          navigate(`/aula-virtual/${id}/content/${nextLesson.id}`, {
            state: { category: nextLesson.category, lessonCompleted: true },
          });
        } else {
          navigate(`/aula-virtual/${id}`);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <Flex
      direction="column"
      gap="md"
      w={isMobile ? "100%" : "75%"}
      m="auto"
    >
      <SatisfactionSurvey
        quiz={satisfactionSurvey}
        enrollmentId={id}
        form={form}
        handleSubmit={handleFormSubmit} 
      />
      <ConfirmModal
        confirmModalOpen={confirmModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        submitValues={submitValues}
        handleSubmit={handleSubmit}
        quiz={satisfactionSurvey}
      />
    </Flex>
  );
};
